import { Controller } from '@hotwired/stimulus';

// Connects to data-controller="payment-element"
export default class extends Controller {
  static targets = ['stripeError', 'stripeNotLoadedError', 'paymentElementForm', 'publicKey', 'customerSessionSecret', 'paymentIntentSecret', 'submitButton'];

  static values = {
    agreementId: String,
    orderReference: String,
    disableAmex: Boolean,
  };

  static appearanceSettings() {
    return {
      variables: {
        colorPrimary: '#0072BF',
        colorText: 'black',
        fontFamily: 'Arial',
        fontSizeXs: '18px',
        fontSizeSm: '18px',
        borderRadius: '0',
        spacingUnit: '6px',
      },
      rules: {
        '.Input': {
          border: '1px solid #696969',
        },
        '.CheckboxInput': {
          border: '1px solid #696969',
        },
        '.RadioIcon': {
          width: '44px',
        },
        '.RadioIconOuter': {
          stroke: '#696969',
          strokeWidth: '2px',
        },
        '.RadioIconInner': {
          r: '16',
          fill: 'black',
        },
      },
    };
  }

  customErrorMessage = {
    fraudulent: 'Your card was declined. Please contact your bank.',
  };

  connect() {
    if (this.constructor.stripeNotLoaded()) {
      this.showNotLoadedError();
    } else {
      this.loadStripePaymentElement();
    }
  }

  static stripeNotLoaded() {
    return typeof Stripe === 'undefined' || Stripe === null;
  }

  showNotLoadedError() {
    if (this.hasStripeNotLoadedErrorTarget) {
      this.stripeNotLoadedErrorTarget.removeAttribute('hidden');
    }
  }

  showErrorMessage(message = null) {
    if (this.hasStripeErrorTarget) {
      if (message) {
        this.stripeErrorTarget.textContent = message;
      }
      this.stripeErrorTarget.removeAttribute('hidden');
    }
  }

  hideErrorMessage() {
    if (this.hasStripeErrorTarget) {
      this.stripeErrorTarget.textContent = '';
      this.stripeErrorTarget.setAttribute('hidden', 'true');
    }
  }

  stripePublicKey() {
    return this.publicKeyTarget.value;
  }

  stripeCustomerSessionSecret() {
    return this.customerSessionSecretTarget.value;
  }

  stripePaymentIntentSecret() {
    return this.paymentIntentSecretTarget.value;
  }

  createStripeElements() {
    const elementsOptions = {
      clientSecret: this.stripePaymentIntentSecret(),
      customerSessionClientSecret: this.stripeCustomerSessionSecret(),
      loader: 'auto',
      appearance: this.constructor.appearanceSettings(),
    };
    if (this.disableAmexValue === true) {
      elementsOptions.disallowedCardBrands = ['american_express'];
    }
    return this.stripeClient.elements(elementsOptions);
  }

  loadStripePaymentElement() {
    this.stripeClient = Stripe(this.stripePublicKey(), { apiVersion: '2020-08-27', betas: ['blocked_card_brands_beta_2'] });
    this.stripeElements = this.createStripeElements();

    const paymentElementOptions = {
      layout: 'accordion',
      fields: {
        billingDetails: {
          name: 'auto',
          email: 'auto',
          address: 'if_required',
        },
      },
    };
    const paymentElement = this.stripeElements.create('payment', paymentElementOptions);
    paymentElement.mount(this.paymentElementFormTarget);
  }

  async confirmPayment() {
    const elements = this.stripeElements;
    const { error: stripeError } = await this.stripeClient.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/agreements/${this.agreementIdValue}/payment/${this.orderReferenceValue}/payment_element_callback`,
        payment_method_data: {
          billing_details: {
            address: {
              country: 'gb',
            },
          },
        },
      },
    });
    if (stripeError) {
      const declineCode = stripeError.decline_code;
      const errorMsg = (declineCode && this.customErrorMessage[declineCode]) || stripeError.message;
      this.showErrorMessage(errorMsg);
      this.submitButtonTarget.value = 'Agree terms and make payment';
      this.submitButtonTarget.disabled = false;
    }
  }

  submit(event) {
    event.preventDefault();
    this.hideErrorMessage();
    this.submitButtonTarget.value = 'Making payment...';
    this.submitButtonTarget.disabled = true;
    this.confirmPayment();
  }
}
